import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Dold portal för godkända handläggare. Länken finns INTE på den publika sajten.
 * Endast e-postadresser i handlaggare_whitelist kan logga in här.
 */
export default function HandlaggareLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const { handlaggareLogin } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const user = await handlaggareLogin(email, password);
      toast.success(`Välkommen ${user?.name || ""}`);
      nav("/dashboard");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Åtkomst nekad");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4" data-testid="handlaggare-login-page">
      {/* Diskret bakgrund som skiljer sig från den publika sidan */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(29,78,216,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-zinc-700 rounded-sm bg-zinc-900/60 text-xs uppercase tracking-widest text-zinc-400">
            <Shield className="w-3 h-3" /> Intern portal
          </div>
          <h1 className="mt-4 text-3xl font-medium text-white" style={{ fontFamily: "Outfit" }}>
            Handläggare-portal
          </h1>
          <p className="mt-2 text-sm text-zinc-500 max-w-sm mx-auto">
            Åtkomst kräver godkänd e-postadress. Kontakta systemägaren för att bli tillagd.
          </p>
        </div>

        <form onSubmit={submit} className="bg-zinc-900/80 backdrop-blur border border-zinc-800 rounded-sm p-6 space-y-4" data-testid="handlaggare-login-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">
              E-post
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              data-testid="handlaggare-email"
              className="w-full px-3 py-2.5 bg-zinc-950 border border-zinc-700 rounded-sm text-white text-sm outline-none focus:border-blue-600"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">
              Lösenord
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="handlaggare-password"
              className="w-full px-3 py-2.5 bg-zinc-950 border border-zinc-700 rounded-sm text-white text-sm outline-none focus:border-blue-600"
            />
          </div>

          {err && (
            <div className="text-xs text-red-400 bg-red-950/40 border border-red-900 rounded-sm px-3 py-2" data-testid="handlaggare-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="handlaggare-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium rounded-sm transition disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Logga in
          </button>
        </form>

        <p className="mt-6 text-[11px] text-zinc-600 text-center">
          Åtkomst loggas. Otillbörliga inloggningsförsök granskas.
        </p>
      </div>
    </div>
  );
}
